import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Star, Flame, Leaf, Sprout } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { formatDualPrice } from '../utils/priceUtils';
import { formatMeasure } from '../utils/measureUtils';

const MenuCard = ({ item, onOpenModal, index }) => {
  const { getName, getDescription, t, language } = useLanguage();
  const { addItem } = useCart();
  const { isDark } = useTheme();
  const [imageLoaded, setImageLoaded] = useState(false);

  const isKidsMenuPart = Boolean(item.includedInKidsMenu);
  const hasMultipleSizes = Object.keys(item.prices).length > 1 && !item.prices.default;
  const displayPrice = item.prices.default || item.prices['30cm'] || Object.values(item.prices)[0];

  // ⭐ НОВА ФУНКЦИЯ ЗА ПОКАЗВАНЕ НА РАЗМЕРА/ГРАМАЖА ⭐
  const getSizeDisplay = () => {
    const sizes = Object.keys(item.prices);
    
    // Ако има само 1 размер
    if (sizes.length === 1) {
      const size = sizes[0];
      // Ако е 'default' - показваме "1 бр."
      if (size === 'default') return language === 'bg' ? '1 бр.' : '1 pc.';
      // Иначе показваме самия размер (напр. "400г", "350г", "200г")
      return formatMeasure(size, language);
    }
    
    // Ако има повече от 1 размер - показваме "от X размер"
    return `${language === 'bg' ? 'от' : 'from'} ${formatMeasure(sizes[0], language)}`;
  };

  // Use image proxy for pizzadonvito.com images
  const getImageUrl = (url) => {
    if (url && url.includes('pizzadonvito.com')) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/image-proxy?url=${encodeURIComponent(url)}`;
    }
    return url;
  };

  const handleQuickAdd = (e) => {
    e.stopPropagation();
    if (hasMultipleSizes) {
      onOpenModal(item);
    } else {
      addItem(item, 'default', 1);
    }
  };

  const preloadImage = () => {
    if (imageLoaded) return;
    const image = new Image();
    image.src = getImageUrl(item.image);
  };

  const tagIcons = {
    top: { icon: Star, color: 'text-yellow-400', bgColor: 'bg-yellow-400/20' },
    new: { icon: Flame, color: 'text-[#C8102E]', bgColor: 'bg-[#C8102E]/20' },
    vegan: { icon: Sprout, color: 'text-emerald-400', bgColor: 'bg-emerald-400/20' },
    vegetarian: { icon: Leaf, color: 'text-[#008000]', bgColor: 'bg-[#008000]/20' },
    spicy: { icon: Flame, color: 'text-orange-400', bgColor: 'bg-orange-400/20' }
  };

  return (
    <motion.div
      data-testid={`menu-card-${item.id}`}
      className={`group relative backdrop-blur-md border rounded-2xl overflow-hidden ${isKidsMenuPart ? 'cursor-default' : 'cursor-pointer'} ${
        isDark 
          ? 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-[#008000]/30' 
          : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-[#008000]/30 shadow-sm hover:shadow-md'
      }`}
      onClick={() => !isKidsMenuPart && onOpenModal(item)}
      onMouseEnter={preloadImage}
      onTouchStart={preloadImage}
    >
      {/* Image Container */}
      <div className={`relative aspect-[4/3] overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
        <img
          src={getImageUrl(item.image)}
          alt={getName(item)}
          className={`w-full h-full transition-opacity duration-200 ${imageLoaded ? 'opacity-100' : 'opacity-0'} ${item.imageContain ? 'object-contain p-8 sm:p-12 bg-white/[0.03]' : 'object-cover'}`}
          loading={index < 2 ? 'eager' : 'lazy'}
          fetchPriority={index === 0 ? 'high' : 'low'}
          decoding="async"
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageLoaded(true)}
        />
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex gap-1 sm:gap-2">
            {item.tags.map(tag => {
              const tagConfig = tagIcons[tag];
              if (!tagConfig) return null;
              const TagIcon = tagConfig.icon;
              return (
                <span 
                  key={tag}
                  className={`flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold ${tagConfig.bgColor} ${tagConfig.color} backdrop-blur-md`}
                >
                  <TagIcon size={10} className="sm:hidden" />
                  <TagIcon size={12} className="hidden sm:block" />
                  <span className="hidden sm:inline">{tag === 'top' ? 'TOP' : tag === 'new' ? 'NEW' : tag === 'vegan' ? 'VEGAN' : tag === 'vegetarian' ? 'VEG' : 'HOT'}</span>
                </span>
              );
            })}
          </div>
        )}

        {/* Quick Add Button */}
        {!isKidsMenuPart && (
        <button
          data-testid={`quick-add-${item.id}`}
          onClick={handleQuickAdd}
          className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 w-8 h-8 sm:w-10 sm:h-10 bg-[#008000] text-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-[#009900] hover:scale-110"
        >
          <Plus size={16} className="sm:hidden" />
          <Plus size={20} className="hidden sm:block" />
        </button>
        )}
      </div>

      {/* Content */}
      <div className="p-2 sm:p-4">
        <h3 className={`font-cormorant text-sm sm:text-lg lg:text-xl font-semibold mb-0.5 sm:mb-1 line-clamp-1 group-hover:text-[#C8102E] transition-colors ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>
          {getName(item)}
        </h3>
        
        <p className={`font-outfit text-xs sm:text-sm mb-2 sm:mb-3 line-clamp-2 min-h-[2rem] sm:min-h-[2.5rem] ${
          isDark ? 'text-white/60' : 'text-gray-500'
        }`}>
          {getDescription(item)}
        </p>

        {/* ⭐ ОБНОВЕНА СЕКЦИЯ ЗА ЦЕНА И РАЗМЕР ⭐ */}
        <div className="flex items-center justify-between">
          {isKidsMenuPart ? (
            <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] sm:text-xs font-semibold ${
              isDark ? 'bg-[#008000]/20 text-emerald-300' : 'bg-green-50 text-[#006B2D]'
            }`}>
              {t('included_in_kids_menu')}
            </span>
          ) : (
          <div className="flex flex-col">
            {/* ВИНАГИ ПОКАЗВА РАЗМЕРА/ГРАМАЖА */}
            <span className={`text-[10px] sm:text-xs font-outfit ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
              {getSizeDisplay()}
            </span>
            <span className="font-outfit text-sm sm:text-lg font-bold text-[#C8102E]">
              {formatDualPrice(displayPrice).eur} €
            </span>
            <span className={`font-outfit text-[10px] sm:text-xs ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
              {formatDualPrice(displayPrice).bgn} {t('currency')}
            </span>
          </div>
          )}
          
          {/* ПОКАЗВА БРОЯ РАЗМЕРИ АКО ИМА ПОВЕЧЕ ОТ 1 */}
          {!isKidsMenuPart && Object.keys(item.prices).length > 1 && (
            <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full hidden sm:inline ${
              isDark ? 'text-white/50 bg-white/5' : 'text-gray-500 bg-gray-100'
            }`}>
              {Object.keys(item.prices).length} {t(Object.keys(item.prices).length === 1 ? 'size' : 'sizes')}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default MenuCard;


